/**
 * Company Name to Ticker Symbol Mappings
 *
 * Maps company names (as they appear in broker statements) to their ticker symbols.
 * Used during CSV/OFX import to infer ticker symbols from transaction descriptions.
 *
 * Add new mappings as needed when encountering new companies or name variations.
 */

export const COMPANY_TICKER_MAP: Record<string, string> = {
  // Individual Stocks - Mega Caps
  'APPLE': 'AAPL',
  'APPLE INC': 'AAPL',
  'MICROSOFT': 'MSFT',
  'MICROSOFT CORP': 'MSFT',
  'AMAZON': 'AMZN',
  'AMAZON COM': 'AMZN',
  'AMAZON.COM': 'AMZN',
  'ALPHABET': 'GOOGL',
  'ALPHABET INC': 'GOOGL',
  'GOOGLE': 'GOOGL',
  'META PLATFORMS': 'META',
  'META': 'META',
  'FACEBOOK': 'META',
  'INTEL': 'INTC',
  'INTEL CORP': 'INTC',

  // Tech & Growth
  'ADVANCED MICRO': 'AMD',
  'ADVANCED MICRO DEVI': 'AMD',
  'ADVANCED MICRO DEVICES': 'AMD',
  'AMD': 'AMD',
  'NUSCALE POWER': 'SMR',
  'NUSCALE POWER CORP': 'SMR',
  'ROCKET LAB': 'RKLB',
  'ROCKET LAB USA': 'RKLB',
  'ROCKET LAB CORP': 'RKLB',
  'SUPER MICRO': 'SMCI',
  'SUPER MICRO COMPUTER': 'SMCI',
  'RIGETTI COMPUTING': 'RGTI',
  'MARA HOLDINGS': 'MARA',
  'ORACLE CORP': 'ORCL',
  'ORACLE': 'ORCL',
  'OKLO': 'OKLO',
  'OKLO INC': 'OKLO',
  'HEWLETT PACKARD': 'HPE',
  'HEWLETT PACKARD ENTERPRISE': 'HPE',
  'ANAVEX': 'AVXL',
  'ANAVEX LIFE': 'AVXL',
  'ANAVEX LIFE SCIENCES': 'AVXL',
  'RYMAN HOSPITALITY': 'RHP',
  'MERCADOLIBRE': 'MELI',
  'PALANTIR': 'PLTR',
  'PALANTIR TECHS': 'PLTR',
  'PALANTIR TECHNOLOGIES': 'PLTR',
  'TESLA': 'TSLA',
  'TESLA INC': 'TSLA',
  'SNAP': 'SNAP',
  'SNAP INC': 'SNAP',
  'NVIDIA': 'NVDA',
  'NVIDIA CORP': 'NVDA',
  'SNOWFLAKE': 'SNOW',
  'SNOWFLAKE INC': 'SNOW',
  'JOBY AVIATION': 'JOBY',
  'JOBY AVIATION INC': 'JOBY',
  'CIPHER MINING': 'CIFR',
  'CIPHER MINING INC': 'CIFR',
  'APPLIED DIGITAL': 'APLD',
  'C3 AI': 'AI',
  'C3.AI': 'AI',
  'C3 AI INC': 'AI',
  'KRATOS': 'KTOS',
  'KRATOS DFNSE': 'KTOS',
  'WARNER BROS': 'WBD',
  'WARNER BROS DISCOVERY': 'WBD',
  'MIRUM': 'MIRM',
  'MIRUM PHARMACEUTICALS': 'MIRM',
  'ASTERA LABS': 'ALAB',
  'ASTERA LABS INC': 'ALAB',
  'DOORDASH': 'DASH',
  'DOORDASH INC': 'DASH',
  'RIOT BLOCKCHAIN': 'RIOT', // Vanguard still uses Riot Platforms' former name
  'RIOT PLATFORMS': 'RIOT',
  'IREN': 'IREN',
  'IREN LTD': 'IREN',
  'KRATOS DFNSE&AMP;SEC': 'KTOS', // HTML entity version from OFX
  'COINBASE': 'COIN',
  'COINBASE GLOBAL': 'COIN',
  'COINBASE GLOBAL INC': 'COIN',
  'ROBINHOOD': 'HOOD',
  'ROBINHOOD MKTS': 'HOOD',
  'ROBINHOOD MARKETS': 'HOOD',
  'CROWDSTRIKE': 'CRWD',
  'CROWDSTRIKE HLDGS': 'CRWD',
  'CROWDSTRIKE HOLDINGS': 'CRWD',
  'SALESFORCE': 'CRM',
  'SALESFORCE INC': 'CRM',
  'MICROSTRATEGY': 'MSTR',
  'MICROSTRATEGY INC': 'MSTR',
  'GAMESTOP': 'GME',
  'GAMESTOP CORP': 'GME',
  'AMC': 'AMC',
  'AMC ENTERTAINMNT': 'AMC',
  'AMC ENTERTAINMENT': 'AMC',
  'IONQ': 'IONQ',
  'IONQ INC': 'IONQ',
  'SOFI': 'SOFI',
  'SOFI TECHNOLOGIES': 'SOFI',
  'SOFI TECH': 'SOFI',

  // Vanguard ETFs
  'VANGUARD TOTAL STOCK MARKET': 'VTI',
  'VANGUARD TOTAL STK MKT': 'VTI',
  'VANGUARD TOTAL STK': 'VTI',
  'VNGRD TTL STK MKT': 'VTI',
  'VANGUARD TOTAL INTL': 'VXUS',
  'VANGUARD TOTAL INTERNATIONAL': 'VXUS',
  'VNGRD TTL INTL': 'VXUS',
  'VNGRD TTL INTL STK': 'VXUS',
  'VANGUARD MUNICIPAL': 'VTEB',
  'SCHWAB DIVIDEND': 'SCHD',
  'VANGUARD FEDERAL MONEY MARKET': 'VMFXX',
  'VANGUARD TREASURY MONEY MARKET': 'VUSXX',

  // iShares ETFs
  'ISHARES RUSSELL 2000 INDEX': 'IWM',
  'ISHARES RUSSELL 2000': 'IWM',
  'ISHARES RUSSELL': 'IWM',
  'ISHR RUSSELL': 'IWM',
  'ISHARES BITCOIN': 'IBIT',
  'ISHR BITCOIN': 'IBIT',
  'ISHR BITCOIN TR': 'IBIT',
  'ISHARES BITCOIN TR': 'IBIT',
  'ISHARES BITCOIN TRUST': 'IBIT',
  'ISHARES S&P 500': 'IVV',
  'ISHARES CORE S&P': 'IVV',
  'ISHARES MSCI EAFE': 'EFA',
  'ISHARES MSCI EMERGING': 'EEM',

  // SPDR ETFs
  'SPDR S&P 500': 'SPY',
  'SPDR GOLD': 'GLD',
  'SPDR GOLD SHARES': 'GLD',

  // Invesco ETFs
  'INVESCO QQQ': 'QQQ',
  'INVESCO QQQ SER 1': 'QQQ',
  'INVESCO QQQ TRUST': 'QQQ',
  'QQQ': 'QQQ',
};
